"use client";

import { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    Grid,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    Snackbar,
    Alert,
    AlertTitle,
    MenuItem,
    InputAdornment,
    Select,
    ListSubheader
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';

import { sendDemoRequest } from '@/lib/contact';
import { saveToSheet } from '@/lib/googleSheet';
import { useTranslations, useLocale } from 'next-intl';
import { countryCodes } from '@/data/contact';

const DemoRequestForm = () => {
    const t = useTranslations('ContactPage.DemoRequestForm');
    const locale = useLocale();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        companyName: '',
        phoneNumber: '',
        message: ''
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [phonePrefix, setPhonePrefix] = useState('+81');
    const [searchPrefix, setSearchPrefix] = useState('');

    const displayedCountries = useMemo(() => {
        const term = searchPrefix.toLowerCase();
        return countryCodes.filter(c =>
            c.label.toLowerCase().includes(term) ||
            c.value.toLowerCase().includes(term) ||
            (c.name && c.name.toLowerCase().includes(term))
        );
    }, [searchPrefix]);

    const MAX_SUBMISSIONS = 3;
    const TIME_WINDOW = 5 * 60 * 1000; // 5 minutes
    const LOCKOUT_DURATION = 60 * 60 * 1000; // 1 hour

    useEffect(() => {
        checkRateLimit();
    }, []);

    const checkRateLimit = () => {
        const lockoutUntil = localStorage.getItem('demo_form_lockout');
        if (lockoutUntil && Date.now() < parseInt(lockoutUntil, 10)) {
            setIsRateLimited(true);
            return true;
        } else if (lockoutUntil) {
            localStorage.removeItem('demo_form_lockout');
            localStorage.removeItem('demo_form_submissions');
            setIsRateLimited(false);
        }
        return false;
    };

    const registerSubmission = () => {
        const now = Date.now();
        const storedSubmissions = JSON.parse(localStorage.getItem('demo_form_submissions') || '[]');
        const validSubmissions = storedSubmissions.filter((timestamp: number) => now - timestamp < TIME_WINDOW);

        validSubmissions.push(now);
        localStorage.setItem('demo_form_submissions', JSON.stringify(validSubmissions));

        if (validSubmissions.length >= MAX_SUBMISSIONS) {
            const lockoutTime = now + LOCKOUT_DURATION;
            localStorage.setItem('demo_form_lockout', lockoutTime.toString());
            setIsRateLimited(true);
        }
    };

    const validate = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.name.trim()) newErrors.name = t('name_error');

        if (!formData.email.trim()) {
            newErrors.email = t('email_error_req');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            newErrors.email = t('email_error_invalid');
        }

        // Phone is optional: only validate if entered
        if (formData.phoneNumber.trim() && !/^[0-9]{5,15}$/.test(formData.phoneNumber.trim())) {
            newErrors.phoneNumber = t('phone_error_invalid');
        }

        if (!formData.message.trim()) newErrors.message = t('message_error');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        let newValue = value;
        if (name === 'phoneNumber') {
            newValue = value.replace(/[^\d]/g, '');
        }

        setFormData(prev => ({ ...prev, [name]: newValue }));

        if (name === 'phoneNumber') {
            const val = newValue.trim();
            if (val && !/^[0-9]{5,15}$/.test(val)) {
                setErrors(prev => ({ ...prev, phoneNumber: t('phone_error_invalid') }));
            } else {
                setErrors(prev => {
                    const newErr = { ...prev };
                    delete newErr.phoneNumber;
                    return newErr;
                });
            }
        } else if (name === 'email') {
            const val = newValue.trim();
            if (!val) {
                setErrors(prev => ({ ...prev, email: t('email_error_req') }));
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                setErrors(prev => ({ ...prev, email: t('email_error_invalid') }));
            } else {
                setErrors(prev => {
                    const newErr = { ...prev };
                    delete newErr.email;
                    return newErr;
                });
            }
        } else {
            if (errors[name]) {
                setErrors(prev => {
                    const newErr = { ...prev };
                    delete newErr[name];
                    return newErr;
                });
            }
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name === 'phoneNumber') {
            const val = value.trim();
            if (val && !/^[0-9]{5,15}$/.test(val)) {
                setErrors(prev => ({ ...prev, phoneNumber: t('phone_error_invalid') }));
            }
        } else if (name === 'email') {
            const val = value.trim();
            if (!val) {
                setErrors(prev => ({ ...prev, email: t('email_error_req') }));
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                setErrors(prev => ({ ...prev, email: t('email_error_invalid') }));
            }
        }
    };

    const handleSubmit = async () => {
        if (isRateLimited || checkRateLimit()) return;
        if (!validate()) return;

        setLoading(true);
        setError(null);

        try {
            const fullPhoneNumber = formData.phoneNumber.trim()
                ? `${phonePrefix}-${formData.phoneNumber.trim()}`
                : '-';
            const apiLanguage = locale === 'ja' ? 'japanese' : 'english';

            // 1. Submit to PMS API
            await sendDemoRequest({
                guest_name: formData.name.trim(),
                guest_email: formData.email.trim(),
                language: apiLanguage,
            });

            // 2. Save full details to Google Sheet
            try {
                await saveToSheet({
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    phoneNumber: fullPhoneNumber,
                    companyName: formData.companyName.trim() || '-',
                    roomCount: '-',
                    source: locale === 'ja' ? 'デモ体験リクエスト' : 'Demo Request',
                    message: formData.message.trim(),
                });
            } catch (sheetErr) {
                console.error('Failed to log demo request to Google Sheet:', sheetErr);
            }

            setSuccess(true);
            registerSubmission();

            // Clear form
            setFormData({
                name: '',
                email: '',
                companyName: '',
                phoneNumber: '',
                message: ''
            });
        } catch (err: any) {
            setError(err?.message || t('error_msg'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 3, md: 5 },
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
        >
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={!!error}
                autoHideDuration={4000}
                onClose={() => setError(null)}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error || t('error_msg')}
                </Alert>
            </Snackbar>

            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
                {t('title')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b', mb: 3 }}>
                {t('description')}
            </Typography>

            {/* Prominent Notice Banner: Guest will get demo link via email */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2,
                    mb: 4,
                    borderRadius: '10px',
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                }}
            >
                <MarkEmailReadOutlinedIcon sx={{ color: '#2563eb', fontSize: 24, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af' }}>
                    {t('email_notice')}
                </Typography>
            </Box>

            {/* In-page Success Notification */}
            {success && (
                <Alert
                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                    severity="success"
                    onClose={() => setSuccess(false)}
                    sx={{
                        mb: 4,
                        borderRadius: '12px',
                        backgroundColor: '#f0fdf4',
                        color: '#166534',
                        border: '1px solid #bbf7d0',
                        '& .MuiAlert-icon': { color: '#16a34a' }
                    }}
                >
                    <AlertTitle sx={{ fontWeight: 700 }}>{t('success_title')}</AlertTitle>
                    {t('success_msg')}
                </Alert>
            )}

            <Grid container spacing={4}>
                {/* Name (Required) */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('name_label_part1')} <Box component="span" sx={{ color: '#ef4444' }}>*</Box> {t('name_label_part2')}
                    </Typography>
                    <TextField
                        fullWidth
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        placeholder={t('name_placeholder')}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Email (Required) */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('email_label_part1')} <Box component="span" sx={{ color: '#ef4444' }}>*</Box> {t('email_label_part2')}
                    </Typography>
                    <TextField
                        fullWidth
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.email}
                        helperText={errors.email}
                        placeholder={t('email_placeholder')}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Phone Number (Optional) */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('phone_label')}
                    </Typography>
                    <TextField
                        fullWidth
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber}
                        placeholder={t('phone_placeholder')}
                        variant="outlined"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Select
                                            variant="standard"
                                            disableUnderline
                                            value={phonePrefix}
                                            onChange={(e) => setPhonePrefix(e.target.value)}
                                            onClose={() => setSearchPrefix('')}
                                            renderValue={(selected) => {
                                                const country = countryCodes.find(c => c.value === selected);
                                                return country ? `${country.flag} ${country.label}` : selected as string;
                                            }}
                                            MenuProps={{
                                                autoFocus: false,
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                        width: 250,
                                                    },
                                                },
                                            }}
                                            sx={{
                                                color: '#64748b',
                                                fontSize: '1rem',
                                                minWidth: '80px',
                                                '& .MuiSelect-select': { py: 0, pl: 0, pr: 2 },
                                            }}
                                        >
                                            <ListSubheader sx={{ pt: 1, pb: 1, zIndex: 2 }}>
                                                <TextField
                                                    size="small"
                                                    autoFocus
                                                    placeholder="Search prefix or country"
                                                    fullWidth
                                                    value={searchPrefix}
                                                    onChange={(e) => setSearchPrefix(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key !== 'Escape') {
                                                            e.stopPropagation();
                                                        }
                                                    }}
                                                />
                                            </ListSubheader>
                                            {displayedCountries.length === 0 ? (
                                                <MenuItem disabled>No results found</MenuItem>
                                            ) : (
                                                displayedCountries.map((country) => (
                                                    <MenuItem key={country.value} value={country.value}>
                                                        {country.flag} {country.name} ({country.label})
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </InputAdornment>
                                ),
                            }
                        }}
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Company Name (Optional) */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('company_label')}
                    </Typography>
                    <TextField
                        fullWidth
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder={t('company_placeholder')}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Message (Required) */}
                <Grid size={{ xs: 12, md: 12 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#475569', mb: 1 }}>
                        {t('message_label_part1')} <Box component="span" sx={{ color: '#ef4444' }}>*</Box> {t('message_label_part2')}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        error={!!errors.message}
                        helperText={errors.message}
                        placeholder={t('message_placeholder')}
                        variant="outlined"
                        sx={{ backgroundColor: '#f9fafb', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Grid>

                {/* Submit Button */}
                <Grid size={{ xs: 12, md: 12 }}>
                    <Button
                        id="demo_button_submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleSubmit}
                        disabled={loading || isRateLimited}
                        sx={{
                            backgroundColor: isRateLimited ? '#94a3b8' : '#F15859',
                            fontWeight: 'bold',
                            py: 2,
                            fontSize: '1rem',
                            borderRadius: '8px',
                            boxShadow: isRateLimited ? 'none' : '0 4px 6px -1px rgba(225, 29, 72, 0.3)',
                            '&:hover': {
                                backgroundColor: isRateLimited ? '#94a3b8' : '#be123c'
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : isRateLimited ? t('submit_btn_rate_limited') : t('submit_btn')}
                    </Button>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: '#64748b' }}>
                        {t('submit_notice')}
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default DemoRequestForm;
